import { StateCreator } from 'zustand';

export interface Document {
  id: number;
  name: string;
  patient: string;
  category:
    'Assessment' | 'Medication' | 'Care Plan' | 'Lab Results' | 'Discharge';
  uploadDate: string;
  size: string;
  type: 'pdf' | 'docx' | 'jpg' | 'png';
  status: 'Reviewed' | 'Pending' | 'New';
}

export interface DocumentsSlice {
  documents: Document[];
  selectedDocument: Document | null;
  isDocumentsLoading: boolean;
  searchTerm: string;
  selectedCategory: string;
  setDocuments: (documents: Document[]) => void;
  addDocument: (document: Omit<Document, 'id'>) => void;
  updateDocument: (id: number, updates: Partial<Document>) => void;
  deleteDocument: (id: number) => void;
  setSelectedDocument: (document: Document | null) => void;
  setDocumentsLoading: (loading: boolean) => void;
  setSearchTerm: (term: string) => void;
  setSelectedCategory: (category: string) => void;
  getFilteredDocuments: () => Document[];
  getDocumentStats: () => {
    total: number;
    reviewed: number;
    pending: number;
    new: number;
  };
}

const mockDocuments: Document[] = [
  {
    id: 1,
    name: 'Patient Assessment - Sarah Johnson.pdf',
    patient: 'Sarah Johnson',
    category: 'Assessment',
    uploadDate: '2024-01-15',
    size: '2.4 MB',
    type: 'pdf',
    status: 'Reviewed',
  },
  {
    id: 2,
    name: 'Medication List - Michael Chen.docx',
    patient: 'Michael Chen',
    category: 'Medication',
    uploadDate: '2024-01-14',
    size: '1.2 MB',
    type: 'docx',
    status: 'Pending',
  },
  {
    id: 3,
    name: 'Care Plan - Emily Davis.pdf',
    patient: 'Emily Davis',
    category: 'Care Plan',
    uploadDate: '2024-01-13',
    size: '3.1 MB',
    type: 'pdf',
    status: 'Reviewed',
  },
  {
    id: 4,
    name: 'Lab Results - Robert Wilson.pdf',
    patient: 'Robert Wilson',
    category: 'Lab Results',
    uploadDate: '2024-01-12',
    size: '1.8 MB',
    type: 'pdf',
    status: 'New',
  },
  {
    id: 5,
    name: 'Discharge Summary - Lisa Thompson.pdf',
    patient: 'Lisa Thompson',
    category: 'Discharge',
    uploadDate: '2024-01-11',
    size: '2.7 MB',
    type: 'pdf',
    status: 'Reviewed',
  },
];

export const documentsSlice: StateCreator<DocumentsSlice> = (set, get) => ({
  documents: mockDocuments,
  selectedDocument: null,
  isDocumentsLoading: false,
  searchTerm: '',
  selectedCategory: 'all',

  setDocuments: (documents: Document[]) => {
    set({ documents });
  },

  addDocument: (document: Omit<Document, 'id'>) => {
    const newDocument: Document = {
      ...document,
      id: Math.max(...get().documents.map(d => d.id)) + 1,
    };
    set(state => ({ documents: [...state.documents, newDocument] }));
  },

  updateDocument: (id: number, updates: Partial<Document>) => {
    set(state => ({
      documents: state.documents.map(document =>
        document.id === id ? { ...document, ...updates } : document
      ),
    }));
  },

  deleteDocument: (id: number) => {
    set(state => ({
      documents: state.documents.filter(document => document.id !== id),
    }));
  },

  setSelectedDocument: (document: Document | null) => {
    set({ selectedDocument: document });
  },

  setDocumentsLoading: (loading: boolean) => {
    set({ isDocumentsLoading: loading });
  },

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  setSelectedCategory: (category: string) => {
    set({ selectedCategory: category });
  },

  getFilteredDocuments: () => {
    const { documents, searchTerm, selectedCategory } = get();
    return documents.filter(doc => {
      const matchesSearch =
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.patient.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' ||
        doc.category.toLowerCase() === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  },

  getDocumentStats: () => {
    const { documents } = get();
    return {
      total: documents.length,
      reviewed: documents.filter(d => d.status === 'Reviewed').length,
      pending: documents.filter(d => d.status === 'Pending').length,
      new: documents.filter(d => d.status === 'New').length,
    };
  },
});
